import { Timestamp } from 'firebase/firestore';
import { FormControl, FormGroup } from "@angular/forms";
import { AssessmentPdfGenerator } from "src/app/patients/dashboard-page/assessments-history-tab/assessment-to-pdf";
import { ControlsOf, AssessmentHelpers } from "./shared";

interface GastrointestinalFields {
  // Bowel sounds

  /**
   * The bowel sounds occurring in the patient's right upper quadrant.
   *
   * Options: hypoactive, hyperactive, or absent.
   */
  bowelSoundsRuq: string;

  /**
   * The bowel sounds occurring in the patient's right lower quadrant.
   *
   * Options: hypoactive, hyperactive, or absent.
   */
  bowelSoundsRlq: string;

  /**
   * The bowel sounds occurring in the patient's left upper quadrant.
   *
   * Options: hypoactive, hyperactive, or absent.
   */
  bowelSoundsLuq: string;

  /**
   * The bowel sounds occurring in the patient's left lower quadrant.
   *
   * Options: hypoactive, hyperactive, or absent.
   */
  bowelSoundsLlq: string;

  /**
   * The date of the patient's last bowel movement.
   */
  dateLastBM: Timestamp | null;

  /**
   * Whether the patient is nauseous (true) or not (false).
   */
  nausea: boolean;

  /**
   * The amount of the patient's emesis (vomit).
   *
   * Options: small, medium, or large.
   */
  emesis: string;

  /**
   * Whether the patient's bowel control is incontinent (true) or not (false).
   */
  incontinence: boolean;

  /**
   * Whether hemorrhoids is present (true) or absent (false).
   */
  hemorrhoids: boolean;

  // Ostomy

  /**
   * The color of the patient's stoma.
   *
   * Options: pink, beefy red, pale, or purple.
   */
  ostomyStomaSite: string;

  /**
   * Describes the patient's output.
   *
   * Options: loose, watery, or formed.
   */
  ostomyOutput: string;

  oralDiet: string;
  tubeFeed: boolean;
  tubeType: string;
  bagChangedHours: string;
  siteAssessment: string;
  reducedAppetite: boolean;
  weightLossPercentage: string;

  nutritionScore: string;
}
export type GastrointestinalFormGroup = FormGroup<ControlsOf<GastrointestinalFields>>;

export const GastrointestinalAssessmentHelpers: AssessmentHelpers = {
  createFormGroup: createGastrointestinalFormGroup,
  toPDF: gastrointestinalAssessmentToPDF,
  formToObject: formToGastrointestinalAssessment,
  initialState: {
    bowelSoundsRuq: '', // hypoactive, hyperactive, absent
    bowelSoundsRlq: '',
    bowelSoundsLuq: '',
    bowelSoundsLlq: '',
    dateLastBM: null,
    nausea: false,
    emesis: '', // small, medium, large
    incontinence: false,
    hemorrhoids: false, // present (true), absent (false)
    ostomyStomaSite: '', // pink, beefy red, pale, purple
    ostomyOutput: '', // loose, watery, formed
    oralDiet: '',
    tubeFeed: false,
    tubeType: '',
    bagChangedHours: '',
    siteAssessment: '',
    reducedAppetite: '',
    weightLossPercentage: '',
    nutritionScore: '',
  },
};


/**
 * Holds information about the patient's abdominal/gastrointestinal assessment.
 */
export class GastrointestinalAssessment {
  bowelSoundsRuq: string;
  bowelSoundsRlq: string;
  bowelSoundsLuq: string;
  bowelSoundsLlq: string;
  dateLastBM: Timestamp | null;
  nausea: boolean;
  emesis: string;
  incontinence: boolean;
  hemorrhoids: boolean;
  ostomyStomaSite: string;
  ostomyOutput: string;
  oralDiet: string;
  tubeFeed: boolean;
  tubeType: string;
  bagChangedHours: string;
  siteAssessment: string;
  reducedAppetite: boolean;
  weightLossPercentage: string;
  nutritionScore: string;

  constructor(data: Partial<GastrointestinalFields>) {
    const is = GastrointestinalAssessmentHelpers.initialState;
    this.bowelSoundsRuq = data.bowelSoundsRuq || is.bowelSoundsRuq;
    this.bowelSoundsRlq = data.bowelSoundsRlq || is.bowelSoundsRlq;
    this.bowelSoundsLuq = data.bowelSoundsLuq || is.bowelSoundsLuq;
    this.bowelSoundsLlq = data.bowelSoundsLlq || is.bowelSoundsLlq;
    this.dateLastBM = data.dateLastBM || is.dateLastBM;
    this.nausea = data.nausea || is.nausea;
    this.emesis = data.emesis || is.emesis;
    this.incontinence = data.incontinence || is.incontinence;
    this.hemorrhoids = data.hemorrhoids || is.hemorrhoids;
    this.ostomyStomaSite = data.ostomyStomaSite || is.ostomyStomaSite;
    this.ostomyOutput = data.ostomyOutput || is.ostomyOutput;
    this.oralDiet = data.oralDiet || is.oralDiet;
    this.tubeFeed = data.tubeFeed || is.tubeFeed;
    this.tubeType = data.tubeType || is.tubeType;
    this.bagChangedHours = data.bagChangedHours || is.bagChangedHours;
    this.siteAssessment = data.siteAssessment || is.siteAssessment;
    this.reducedAppetite = data.reducedAppetite || is.reducedAppetite;
    this.weightLossPercentage = data.weightLossPercentage || is.weightLossPercentage;
    this.nutritionScore = data.nutritionScore || is.nutritionScore;
  }
}

function createGastrointestinalFormGroup(gastrointestinalAssessment?: GastrointestinalAssessment) {
  const data = gastrointestinalAssessment || new GastrointestinalAssessment(GastrointestinalAssessmentHelpers.initialState);
  return new FormGroup<ControlsOf<GastrointestinalFields>>({
    bowelSoundsRuq: new FormControl<string | null>(data.bowelSoundsRuq),
    bowelSoundsRlq: new FormControl<string | null>(data.bowelSoundsRlq),
    bowelSoundsLuq: new FormControl<string | null>(data.bowelSoundsLuq),
    bowelSoundsLlq: new FormControl<string | null>(data.bowelSoundsLlq),
    dateLastBM: new FormControl<Timestamp | null>(data.dateLastBM),
    nausea: new FormControl<boolean | null>(data.nausea),
    emesis: new FormControl<string | null>(data.emesis),
    incontinence: new FormControl<boolean | null>(data.incontinence),
    hemorrhoids: new FormControl<boolean | null>(data.hemorrhoids),
    ostomyStomaSite: new FormControl<string | null>(data.ostomyStomaSite),
    ostomyOutput: new FormControl<string | null>(data.ostomyOutput),
    oralDiet: new FormControl<string | null>(data.oralDiet),
    tubeFeed: new FormControl<boolean | null>(data.tubeFeed),
    tubeType: new FormControl<string | null>(data.tubeType),
    bagChangedHours: new FormControl<string | null>(data.bagChangedHours),
    siteAssessment: new FormControl<string | null>(data.siteAssessment),
    reducedAppetite: new FormControl<boolean | null>(data.reducedAppetite),
    weightLossPercentage: new FormControl<string | null>(data.weightLossPercentage),
    nutritionScore: new FormControl<string | null>(data.nutritionScore)
  });
}

function gastrointestinalAssessmentToPDF(pdf: AssessmentPdfGenerator, assessment: GastrointestinalAssessment,
): void {
  pdf.addSectionTitle("Abdominal");
  pdf.pdf.text("Bowel Sounds", pdf.margin, pdf.currY);
  pdf.currY += 20;
  pdf.addTwoColumnText("LUQ", assessment.bowelSoundsLuq, "RUQ", assessment.bowelSoundsRuq);
  pdf.addTwoColumnText("LLQ", assessment.bowelSoundsLlq, "RLQ", assessment.bowelSoundsRlq);
  pdf.addTwoColumnText("Last Bowel Movement", assessment.dateLastBM?.seconds !== 0 ? assessment.dateLastBM!.toDate().toUTCString() : "N/A",
    "Nausea", assessment.nausea ? "Yes" : "No");
  pdf.addTwoColumnText("Emesis", assessment.emesis, "Bowel Control", assessment.incontinence ? "Yes" : "No");
  pdf.currY += 10;
  pdf.pdf.text("Ostomy", pdf.margin, pdf.currY);
  pdf.currY += 20;
  pdf.addTwoColumnText("Stoma Site", assessment.ostomyStomaSite, "Output", assessment.ostomyOutput);
  pdf.currY += 10;
  pdf.pdf.text("Hemorrhoids", pdf.margin, pdf.currY);
  pdf.currY += 20;
  pdf.addTwoColumnText("Hemorrhoids Present", assessment.hemorrhoids ? "Yes" : "No", "", "");
  pdf.currY += 10;
  pdf.pdf.text("Nutrition", pdf.margin, pdf.currY);
  pdf.currY += 20;
  pdf.addTwoColumnText("Reduced Appetite", assessment.reducedAppetite ? "Yes" : "No", "Weight Loss %", assessment.weightLossPercentage);
  pdf.addTwoColumnText("Oral Diet", assessment.oralDiet, "Tube Feed", assessment.tubeFeed ? "Yes" : "No");
  pdf.addOneColumnText("Tube Type", assessment.tubeType);
  pdf.addOneColumnText("Hours Since Bag Change", assessment.bagChangedHours);
  pdf.addOneColumnText("Site Assessment", assessment.siteAssessment);
  pdf.addOneColumnText("Nutrition Score", assessment.nutritionScore);
}

function formToGastrointestinalAssessment(fg: GastrointestinalFormGroup) {
  // @ts-ignore
  const d = new Date(fg.controls.dateLastBM.value!);

  return new GastrointestinalAssessment({
    bowelSoundsRuq: fg.controls.bowelSoundsRuq.value!,
    bowelSoundsRlq: fg.controls.bowelSoundsRlq.value!,
    bowelSoundsLuq: fg.controls.bowelSoundsLuq.value!,
    bowelSoundsLlq: fg.controls.bowelSoundsLlq.value!,
    dateLastBM: Timestamp.fromDate(d),
    nausea: fg.controls.nausea.value!,
    emesis: fg.controls.emesis.value!,
    incontinence: fg.controls.incontinence.value!,
    hemorrhoids: fg.controls.hemorrhoids.value!,
    ostomyStomaSite: fg.controls.ostomyStomaSite.value!,
    ostomyOutput: fg.controls.ostomyOutput.value!,
    oralDiet: fg.controls.oralDiet.value!,
    tubeFeed: fg.controls.tubeFeed.value!,
    tubeType: fg.controls.tubeType.value!,
    bagChangedHours: fg.controls.bagChangedHours.value!,
    siteAssessment: fg.controls.siteAssessment.value!,
    reducedAppetite: fg.controls.reducedAppetite.value!,
    weightLossPercentage: fg.controls.weightLossPercentage.value!,
    nutritionScore: fg.controls.nutritionScore.value!,
  });
}